import * as React from "react";
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  TableContainer,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { formatDateToFrenshFormat } from "../../utils/formatDateToFrenshFormat";

export interface AppointmentRecord {
  _id: string;
  outlet: string;
  scheduledAt: Date | string;
  status: string;
  quantityBin?: number;
  quantityBinToBeCollected?: number;
  executionDate?: Date | string;
}

export enum APPOINTMENT_STATUS_ENUM {
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  CANCELLED = "CANCELLED",
}

interface AppointmentListProps {
  appointments: AppointmentRecord[];
}

const statusChoices = [
  { id: "PENDING", name: "En attente" },
  { id: "CONFIRMED", name: "Confirmé" },
  { id: "CANCELLED", name: "Annulé" },
];

const statusLabels = statusChoices.reduce(
  (acc, { id, name }) => ({ ...acc, [id]: name }),
  {} as Record<string, string>
);

const statusColorMap: Record<string, "success" | "warning" | "error"> = {
  PENDING: "warning",
  CONFIRMED: "success",
  CANCELLED: "error",
};

const API_URL =
  process.env.REACT_APP_API_URL || "https://aihubcy.hubency.com/api";

export const AppointmentList: React.FC<AppointmentListProps> = ({
  appointments,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  // QR dialog state
  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrImageUrl, setQrImageUrl] = React.useState<string | null>(null);
  const [qrLoading, setQrLoading] = React.useState(false);
  const [qrError, setQrError] = React.useState<string | null>(null);

  const inviteToken = new URLSearchParams(useLocation().search).get(
    "inviteToken"
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedAppointments = React.useMemo(
    () =>
      appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [appointments, page, rowsPerPage]
  );

  const fetchQr = async (appointmentId: string) => {
    if (!inviteToken) {
      setQrError("Invite token manquant");
      return setQrOpen(true);
    }
    setQrLoading(true);
    setQrError(null);
    try {
      const res = await fetch(
        `${API_URL}/appointments/${appointmentId}/qr?inviteToken=${inviteToken}`
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setQrImageUrl(url);
    } catch (err: any) {
      setQrError(err.message);
    } finally {
      setQrLoading(false);
      setQrOpen(true);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Vos rendez-vous
        </Typography>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">Date prévue</TableCell>
                <TableCell align="center">Date d'exécution</TableCell>
                <TableCell align="center">Caisses prévues</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell align="center">QR Code</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAppointments.map((a) => (
                <TableRow key={a._id}>
                  <TableCell align="center">
                    {formatDateToFrenshFormat(a.scheduledAt, true)}
                  </TableCell>
                  <TableCell align="center">
                    {a.executionDate
                      ? formatDateToFrenshFormat(a.executionDate)
                      : "-"}
                  </TableCell>
                  <TableCell align="center">{a.quantityBin ?? "-"}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={statusLabels[a.status] ?? a.status}
                      color={statusColorMap[a.status] || "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {a.status === "PENDING" ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => fetchQr(a._id)}
                      >
                        Voir QR
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={appointments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      </Paper>

      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>QR Code du rendez-vous</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          {qrLoading && <CircularProgress />}
          {qrError && <Alert severity="error">{qrError}</Alert>}
          {qrImageUrl && (
            <img
              src={qrImageUrl}
              alt="QR Code"
              style={{ maxWidth: "100%", height: "auto", marginTop: 16 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
